import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { GavelIcon } from '@phosphor-icons/react/dist/csr/Gavel';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { SegmentedControl } from '../../design-system/primitives/SegmentedControl/SegmentedControl';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { justificationsApi, type JustificationRow } from '../../lib/api/justifications';
import { mapDetails } from '../reference/referenceFormHelpers';
import styles from './DecideDialog.module.css';

const FORBIDDEN_CODE = 'forbidden';

type Decision = 'approve' | 'reject';

/**
 * `POST /justifications/{justification}/decide` (M-11, Task 4, spec §2,
 * J-4, J-7).
 *
 * ## `decision_note` is required only when rejecting (spec J-7)
 *
 * `DecideJustificationRequest::rules()`'s own `required_if:decision,reject`
 * is the actual gate — this dialog adds the identical check client-side as
 * a courtesy (so a caller sees it before round-tripping), never as a
 * substitute for it: a `422` naming `decision_note` still renders exactly
 * as the server worded it, through the same `fieldErrors['decision_note']`
 * path the server-side failure would take if this client check were ever
 * wrong or bypassed.
 *
 * ## The server's refusal is not always keyed to a field this form renders
 *
 * `decision`/`decision_note` are the two fields here. A `409` for "not
 * currently `answered`" (spec J-4) names `status`, neither of the two — so
 * it goes to the failure banner rather than being silently dropped, the
 * identical split `EscalationActionDialog.tsx`/`AnswerDialog.tsx` both take
 * for their own out-of-band field.
 */

export interface DecideDialogProps {
  open: boolean;
  /** `null` while nothing is being acted on — this dialog is not mounted with `open` in that state, but the type still allows it so a caller cannot pass a stale uuid by construction. */
  justificationUuid: string | null;
  /** The complaint's own subject name, so the dialog's title names the record rather than asking a bare "Are you sure?" (`Dialog`'s own development guard). */
  subjectName: string;
  onClose: () => void;
  onDone: (updated: JustificationRow) => void;
}

export function DecideDialog({ open, justificationUuid, subjectName, onClose, onDone }: DecideDialogProps) {
  const { t } = useTranslation();

  const [decision, setDecision] = useState<Decision>('approve');
  const [note, setNote] = useState('');
  const [missingNote, setMissingNote] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) return;
    setDecision('approve');
    setNote('');
    setMissingNote(false);
    setFailure(null);
    setFieldErrors({});
  }, [open]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('justifications.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('justifications.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('justifications.decideFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current || justificationUuid === null) return;

    // The server's own `required_if:decision,reject` (spec J-7) — checked
    // here too, as a courtesy, never as a substitute for it (this file's
    // own docblock).
    if (decision === 'reject' && note.trim() === '') {
      setMissingNote(true);
      return;
    }
    setMissingNote(false);

    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setFieldErrors({});

    try {
      const updated = await justificationsApi.decide(justificationUuid, {
        decision,
        decision_note: note.trim() === '' ? null : note.trim(),
      });
      onDone(updated);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;

      const mapped = mapDetails(error.details);

      // THE SERVER'S REFUSAL IS NOT ALWAYS KEYED TO `decision`/`decision_note`
      // — see this file's own docblock. `status` (not currently `answered`)
      // is neither, so it surfaces in the banner instead of being dropped.
      const banner = Object.entries(mapped)
        .filter(([field]) => field !== 'decision' && field !== 'decision_note')
        .map(([, message]) => message);

      setFieldErrors(mapped);

      if (banner.length > 0) {
        setFailure(banner.join(' '));
      } else if (Object.keys(mapped).length === 0) {
        setFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} kind="form" onClose={onClose} title={t('justifications.decideTitle', { subject: subjectName })}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>

        <SegmentedControl
          label={t('justifications.decisionLabel')}
          options={[
            { value: 'approve', label: t('justifications.decisionApprove') },
            { value: 'reject', label: t('justifications.decisionReject') },
          ]}
          value={decision}
          onChange={(value) => {
            setDecision(value as Decision);
            setMissingNote(false);
          }}
        />

        <Textarea
          label={t('justifications.decisionNoteLabel')}
          dir="auto"
          value={note}
          error={missingNote ? t('justifications.decisionNoteRequired') : fieldErrors['decision_note']}
          onChange={(event) => {
            const { value } = event.target;
            setNote(value);
          }}
        />

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<GavelIcon size={16} />}>
            {t('justifications.decideSubmit')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('justifications.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
